import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Divider, Flex, Group, Loader, Select, Stack, Switch, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAllFeatures, useRoleFeatureConfig } from '../../../global/entities/Feature/queries';
import { useSetFeatureEnabled, useSetRoleFeatures } from '../../../global/entities/Feature/mutations';
import { useGetHealthcareProfessionalNew } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalNew.tsx';

const TDM_TASK_TYPE_FEATURE_CODE = 'taskType.tdm';
const PATIENT_ROLE_TYPE_ID = '0';

const RoleFeatureManagement: React.FC = () => {
    const { t } = useTranslation();
    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [enabledIds, setEnabledIds] = useState<number[]>([]);

    const { data: allFeatures, isLoading: isLoadingFeatures } = useAllFeatures();
    const { data: roleCatalogue } = useGetHealthcareProfessionalNew();
    const roleTypeId = selectedRole !== null ? parseInt(selectedRole) : undefined;
    const { data: roleConfig, isLoading: isLoadingConfig } = useRoleFeatureConfig(roleTypeId ?? 0);
    const setRoleFeaturesMutation = useSetRoleFeatures();
    const setFeatureEnabledMutation = useSetFeatureEnabled();

    const tdmFeature = allFeatures?.find(f => f.code === TDM_TASK_TYPE_FEATURE_CODE);

    const handleToggleTdmFeature = async (checked: boolean) => {
        if (!tdmFeature) return;
        await setFeatureEnabledMutation.mutateAsync({ featureId: tdmFeature.id, isEnabled: checked });
        notifications.show({
            title: t('featureManagement.title'),
            message: t('featureManagement.tdmToggleSuccess'),
            color: 'green',
        });
    };

    useEffect(() => {
        if (roleConfig)
            setEnabledIds(roleConfig.enabledFeatureIds);
    }, [roleConfig]);

    const roleOptions = [
        { value: PATIENT_ROLE_TYPE_ID, label: t('featureManagement.patient') },
        ...(roleCatalogue?.healthcareProfessionalTypesCombobox ?? []),
    ];

    const sortedFeatures = [...(allFeatures ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);

    const handleToggle = (featureId: number) => {
        setEnabledIds(prev =>
            prev.includes(featureId)
                ? prev.filter(id => id !== featureId)
                : [...prev, featureId]
        );
    };

    const handleSelectAll = () => {
        if (allFeatures) setEnabledIds(allFeatures.map(f => f.id));
    };

    const handleDeselectAll = () => {
        setEnabledIds([]);
    };

    const handleSave = async () => {
        if (roleTypeId === undefined) return;
        await setRoleFeaturesMutation.mutateAsync({ roleTypeId, featureIds: enabledIds });
        notifications.show({
            title: t('featureManagement.title'),
            message: t('featureManagement.saveSuccess'),
            color: 'green',
        });
    };

    return (
        <Stack pt="xl" gap="lg">
            <Title>{t('featureManagement.title')}</Title>
            <Text c="dimmed" size="sm">{t('featureManagement.description')}</Text>

            <Card withBorder padding="md">
                <Stack gap="xs">
                    <Title order={4}>{t('featureManagement.globalFeaturesTitle')}</Title>
                    {isLoadingFeatures && !tdmFeature ? (
                        <Loader size="sm" />
                    ) : tdmFeature ? (
                        <Switch
                            label={tdmFeature.name}
                            checked={tdmFeature.isEnabled}
                            onChange={(event) => handleToggleTdmFeature(event.currentTarget.checked)}
                            disabled={setFeatureEnabledMutation.isPending}
                        />
                    ) : null}
                </Stack>
            </Card>

            <Divider />

            <Select
                label={t('featureManagement.selectRole')}
                data={roleOptions}
                value={selectedRole}
                onChange={setSelectedRole}
                searchable
                w={420}
            />

            {selectedRole !== null && selectedRole !== PATIENT_ROLE_TYPE_ID && (
                <Text c="dimmed" size="sm">{t('featureManagement.medicalInformationAlwaysVisibleHcp')}</Text>
            )}

            {selectedRole !== null && (isLoadingFeatures || isLoadingConfig) && (
                <Flex justify="center" p="xl"><Loader /></Flex>
            )}

            {selectedRole !== null && allFeatures && !isLoadingConfig && (
                <Stack gap="md">
                    <Group>
                        <Button variant="light" size="xs" onClick={handleSelectAll}>
                            {t('featureManagement.selectAll')}
                        </Button>
                        <Button variant="light" size="xs" color="gray" onClick={handleDeselectAll}>
                            {t('featureManagement.deselectAll')}
                        </Button>
                    </Group>

                    <Card withBorder padding="md">
                        <Stack gap="xs">
                            {sortedFeatures.map(feature => (
                                <Checkbox
                                    key={feature.id}
                                    label={feature.name}
                                    checked={enabledIds.includes(feature.id)}
                                    onChange={() => handleToggle(feature.id)}
                                />
                            ))}
                        </Stack>
                    </Card>

                    <Group>
                        <Button
                            onClick={handleSave}
                            loading={setRoleFeaturesMutation.isPending}
                        >
                            {t('featureManagement.save')}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Stack>
    );
};

export default RoleFeatureManagement;
